import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import cx from "classnames";
import { History } from "history";
import React from "react";

import { getForumCommentUrl } from "@/mk2/apps/forum/urls";
import { getCommentDisplayAuthor } from "@/mk2/apps/forum/utils";
import { RenderSnippetAs } from "@/mk2/apps/wiki/utils";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Clamp } from "@/mk2/components/Clamp";
import {
    FilterAdditionalData,
    ShowPhotosAndVideos,
    astToHtml,
    defaultFilterAdditionalData,
} from "@/mk2/helpers/article_ast/filters";
import { cacheLast } from "@/mk2/helpers/cache";
import { scrollElementTo } from "@/mk2/helpers/scrolling";
import { BlogPostEntity, CommentEntity, ForumMessageEntity, PostEntity } from "@/mk2/schemas";
import { COMMENT_DELETED } from "@/mk/autogenerated/translation";

import styles from "./CommentRepliedToMessage.mscss";

interface OwnProps {
    clamp: number;
    style: "dark" | "light" | "twitter";
    label?: string;
    hasGreyBorder?: boolean;

    repliedToBlogComment?: CommentEntity;

    repliedToForumMessage?: ForumMessageEntity;

    repliedToMessageAuthor?: string;
    repliedToMessageBody?: string;

    forumPost?: PostEntity;
    forumCommentUrl?: string;

    photo?: React.ReactNode;

    history?: History;
    hideTwitterLine?: boolean;
    onRemoveReplyTo?();
}

type Props = OwnProps;

class CommentRepliedToMessage extends React.PureComponent<Props> {
    private astToHtmlCache = cacheLast(true);

    public render() {
        const {
            clamp,
            hasGreyBorder,
            label,
            onRemoveReplyTo,
            repliedToForumMessage,
            repliedToBlogComment,
            style,
            photo,
            forumPost,
            forumCommentUrl,
            history,
            repliedToMessageAuthor,
            repliedToMessageBody,
            hideTwitterLine,
        } = this.props;

        let Wrapper: React.ElementType = React.Fragment;
        let wrapperProps = {};
        if (repliedToForumMessage || forumCommentUrl) {
            const commentId = repliedToForumMessage ? `comment-${repliedToForumMessage.id}` : null;
            Wrapper = "div";
            wrapperProps = {
                onClick: () => {
                    if (commentId && document.getElementById(commentId)) {
                        scrollElementTo(`#${commentId}`, "window-top", 50, true);
                    } else if (history && forumCommentUrl) {
                        // If the comment is not in the DOM, we redirect to the comment URL
                        history.push(forumCommentUrl);
                    } else if (history && forumPost) {
                        // If the comment is not in the DOM, we redirect to the comment URL
                        const gotoUrl = getForumCommentUrl(forumPost, repliedToForumMessage);
                        history.push(gotoUrl);
                    }
                },
            };
        }

        if (repliedToBlogComment) {
            const commentId = `#comment-${repliedToBlogComment.id}`;
            Wrapper = "div";
            wrapperProps = {
                onClick: () => {
                    scrollElementTo(commentId, "window-top", 50, true);
                },
            };
        }

        return (
            <Wrapper {...wrapperProps}>
                <div
                    className={cx(
                        styles.CommentRepliedToMessage,
                        styles[`CommentRepliedToMessage--${style}`],
                        hasGreyBorder && styles.CommentRepliedToMessage__hasGreyBorder,
                    )}
                >
                    {Boolean(
                        label ||
                        repliedToMessageAuthor ||
                        repliedToForumMessage ||
                        repliedToBlogComment ||
                        onRemoveReplyTo,
                    ) && (
                        <div className={styles.CommentRepliedToMessage__firstLine}>
                            {Boolean(
                                label ||
                                repliedToMessageAuthor ||
                                repliedToForumMessage ||
                                repliedToBlogComment,
                            ) && (
                                <div
                                    className={cx(
                                        styles.CommentRepliedToMessage__user,
                                        styles[`CommentRepliedToMessage__user--${style}`],
                                    )}
                                >
                                    {Boolean(label) && <>{label} </>}
                                    {Boolean(
                                        repliedToMessageAuthor ||
                                        repliedToForumMessage ||
                                        repliedToBlogComment,
                                    ) && (
                                        <>
                                            @
                                            {repliedToMessageAuthor ||
                                                getCommentDisplayAuthor(
                                                    repliedToForumMessage || repliedToBlogComment,
                                                )}
                                        </>
                                    )}
                                </div>
                            )}
                            {onRemoveReplyTo && (
                                <div className={styles.CommentRepliedToMessage__closeBtn}>
                                    <Btn
                                        icon={faTimes}
                                        onClick={onRemoveReplyTo}
                                        type={BtnType.BlackText}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    <Clamp clamp={clamp}>
                        <div className={styles.CommentRepliedToMessage__message}>
                            {repliedToMessageBody ||
                                getRepliedToMessageBodyChunks(
                                    repliedToForumMessage || repliedToBlogComment,
                                    style,
                                    this.astToHtmlCache,
                                )}
                        </div>
                    </Clamp>
                    {photo}
                    {style === "twitter" && !hideTwitterLine && (
                        <div className={styles.CommentRepliedToMessage__twitter__line}>&nbsp;</div>
                    )}
                </div>
            </Wrapper>
        );
    }
}

export default CommentRepliedToMessage;

const getRepliedToMessageBodyChunks = (comment, style, cache) => {
    let bodyChunks: React.ReactChild[] = null;
    if (!comment) {
        return null;
    }
    if (comment.deletedAt) {
        return COMMENT_DELETED;
    }
    if (comment.textAST) {
        const additionalData: FilterAdditionalData = {
            ...defaultFilterAdditionalData,
            showPhotosAndVideos: ShowPhotosAndVideos.NONE,
            renderSnippetObjectsAs: RenderSnippetAs.PLAIN_LINK,
            renderSpecialWikiLinks: false,
            customStyles: {
                paragraph: styles[`CommentRepliedToMessage__paragraph--${style}`],
                mention: styles[`CommentRepliedToMessage__mention--${style}`],
            },
            rootDiv: false,
        };
        bodyChunks = cache(() => astToHtml(comment.textAST, additionalData), comment.textAST);
    }
    return bodyChunks;
};
